import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Users, Edit2, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { employeeAPI } from '@/lib/api';
import EmployeeAvailabilityDialog from './EmployeeAvailabilityDialog';

export default function EmployeeManagement() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    daily_hours: 8
  });
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await employeeAPI.getAll();
      setEmployees(response.data);
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingEmployee) {
        await employeeAPI.update(editingEmployee.id, formData);
        toast.success('Employee updated successfully');
      } else {
        await employeeAPI.create(formData);
        toast.success('Employee created successfully');
      }
      
      setFormData({ name: '', email: '', daily_hours: 8 });
      setEditingEmployee(null);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save employee');
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name,
      email: employee.email || '',
      daily_hours: employee.daily_hours
    });
  };

  const handleDelete = async (employeeId, employeeName) => {
    try {
      await employeeAPI.delete(employeeId);
      toast.success(`${employeeName} deleted successfully`);
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to delete employee');
    }
  };

  const handleCancel = () => {
    setEditingEmployee(null);
    setFormData({ name: '', email: '', daily_hours: 8 });
  };

  const moveEmployee = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= employees.length) return;

    // Create new array with swapped positions
    const newEmployees = [...employees];
    [newEmployees[index], newEmployees[newIndex]] = [newEmployees[newIndex], newEmployees[index]];
    
    // Update local state immediately for responsive UI
    setEmployees(newEmployees);

    // Save to backend
    setIsSavingOrder(true);
    try {
      const employeeIds = newEmployees.map(e => e.id);
      await employeeAPI.reorder(employeeIds);
      toast.success('Employee order updated');
    } catch (error) {
      toast.error('Failed to save order');
      // Revert on error
      fetchEmployees();
    } finally {
      setIsSavingOrder(false);
    }
  };

  if (loading) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Employee Management</h1>
          <p className="text-muted-foreground mt-1">Manage employees and their schedules</p>
        </div>
        <Users className="h-8 w-8 text-muted-foreground" />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Add/Edit Employee Form */}
        <Card>
          <CardHeader>
            <CardTitle>{editingEmployee ? 'Edit Employee' : 'Add New Employee'}</CardTitle>
            <CardDescription>
              {editingEmployee ? 'Update employee information' : 'Create a new employee for scheduling'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  required
                />
              </div>

              <div>
                <Label htmlFor="email">Email (optional)</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <Label htmlFor="daily_hours">Daily Hours</Label>
                <Input
                  id="daily_hours"
                  type="number"
                  step="0.5"
                  min="0"
                  max="24"
                  value={formData.daily_hours}
                  onChange={(e) => setFormData({ ...formData, daily_hours: parseFloat(e.target.value) })}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Default working hours per day (Mon-Fri, 8am-5pm = 8 hours)
                </p>
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="flex-1">
                  {editingEmployee ? 'Update' : 'Create'} Employee
                </Button>
                {editingEmployee && (
                  <Button type="button" variant="outline" onClick={handleCancel}>
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Employee List */}
        <Card>
          <CardHeader>
            <CardTitle>Current Employees ({employees.length})</CardTitle>
            <CardDescription>
              Use the arrows to reorder employees on the calendar
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {employees.length === 0 ? (
                <p className="text-muted-foreground text-sm">No employees yet. Add one to get started!</p>
              ) : (
                employees.map((employee, index) => (
                  <div
                    key={employee.id}
                    className="p-3 border rounded-lg space-y-2 bg-white hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      {/* Order controls */}
                      <div className="flex flex-col gap-0.5">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6"
                          onClick={() => moveEmployee(index, -1)}
                          disabled={index === 0 || isSavingOrder}
                          title="Move up"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6"
                          onClick={() => moveEmployee(index, 1)}
                          disabled={index === employees.length - 1 || isSavingOrder}
                          title="Move down"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Position indicator */}
                      <div className="w-6 h-6 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center text-xs font-medium">
                        {index + 1}
                      </div>

                      {/* Employee info */}
                      <div className="flex-1">
                        <p className="font-medium">{employee.name}</p>
                        {employee.email && (
                          <p className="text-xs text-muted-foreground">{employee.email}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {employee.daily_hours} hours/day (default)
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(employee)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        
                        {/* Delete with confirmation dialog */}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="hover:bg-red-100 hover:text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Employee</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete <strong>{employee.name}</strong>? 
                                This will also remove all their schedule assignments.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-red-600 hover:bg-red-700"
                                onClick={() => handleDelete(employee.id, employee.name)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                    <div className="flex justify-end pl-14">
                      <EmployeeAvailabilityDialog employee={employee} />
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
